import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { AccessWindowGate } from './AccessWindowGate';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/** Only these roles may use this app; others (e.g. staff, manager) are blocked after login. */
const ALLOWED_APP_ROLES = new Set(['agent', 'admin', 'superagent']);

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { session, loading, branchesLoading, userProfile, signOut } = useAuth();
  const location = useLocation();

  if (loading || branchesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!userProfile) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, textAlign: 'center' }}>
          Account unavailable
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 420 }}>
          Your profile could not be loaded. Try again or contact support.
        </Typography>
        <Button variant="contained" onClick={() => void signOut()} sx={{ textTransform: 'none' }}>
          Sign out
        </Button>
      </Box>
    );
  }

  if (!ALLOWED_APP_ROLES.has(userProfile.role)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, textAlign: 'center' }}>
          Access not allowed
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1.5, textAlign: 'center', maxWidth: 480 }}>
          This application is only for <strong>agent</strong>, <strong>admin</strong>, and{' '}
          <strong>superagent</strong> accounts. Your role ({userProfile.role}) cannot use this app.
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 480 }}>
          If you have a <strong>staff</strong> or <strong>manager</strong> role, use the other app:{' '}
          <Link href="https://receipts.samofund.net" target="_blank" rel="noopener noreferrer">
            receipts.samofund.net
          </Link>
          .
        </Typography>
        <Button variant="contained" onClick={() => void signOut()} sx={{ textTransform: 'none' }}>
          Sign out
        </Button>
      </Box>
    );
  }

  return <AccessWindowGate userRole={userProfile.role}>{children}</AccessWindowGate>;
}
